import { normalizeInstrument } from './helpers';

// ── SCORING.JS ────────────────────────────────────────────────────────────────
// Calcolo client-side del PriorityScore e della DistanzaDiFrequenza.
//
// Formula ranking: likes + coverage * 2
//   - likes:    valore assoluto dei like
//   - coverage: numero di musicisti nel brano
//
// DistanzaDiFrequenza: misura di equità nelle presenze per strumento.
//   Per ogni strumento: sum(maxPlays - eachPlay) per tutti i musicisti di quel strumento.
//   DistanzaAttuale = somma su tutti gli strumenti.
//   Delta per brano = DistanzaTeorica(brano simulato) - DistanzaAttuale.
//   Delta < 0 → riduce diseguaglianza (buono, teal)
//   Delta = 0 → neutro (muted)
//   Delta > 0 → peggiora diseguaglianza (arancione)

// ── NORMALIZZAZIONE RUOLO → STRUMENTO ────────────────────────────────────────
// Le regole stanno in helpers.js, in un posto solo: qui si aggiunge solo il
// fatto che un ruolo vuoto vale come "Altro".
function normalizeInstrumentRole(role) {
  return normalizeInstrument(role) || 'Altro';
}

// ── BRANI SUONATI PER MUSICISTA-STRUMENTO ────────────────────────────────────
// Chiave: "musicianId__strumento"
// Solo brani con played=true.
export function countPlayedPerMusicianInstrument(allTracks) {
  const map = {};
  for (const track of allTracks) {
    if (!track.played) continue;
    for (const player of (track.track_players || [])) {
      const id    = String(player.musician_id);
      const instr = normalizeInstrumentRole(player.role);
      const key   = `${id}__${instr}`;
      map[key] = (map[key] || 0) + 1;
    }
  }
  return map;
}

// ── MUSICISTI PER STRUMENTO ──────────────────────────────────────────────────
// Tutti i musicisti (brani suonati e non) per categoria strumento.
// {strumento: Set<musicianId>}
export function getMusiciansPerInstrument(allTracks) {
  const map = {};
  for (const track of allTracks) {
    for (const player of (track.track_players || [])) {
      const instr = normalizeInstrumentRole(player.role);
      if (!map[instr]) map[instr] = new Set();
      map[instr].add(String(player.musician_id));
    }
  }
  return map;
}

// ── DISTANZA DI FREQUENZA ATTUALE ────────────────────────────────────────────
// Per ogni strumento: sum(maxPlays - eachPlay) su tutti i musicisti.
// Totale = somma su tutti gli strumenti.
export function computeCurrentFrequencyDistance(playsMap, musiciansMap) {
  let total = 0;
  for (const [instr, musicianSet] of Object.entries(musiciansMap)) {
    const counts = [...musicianSet].map(id => playsMap[`${id}__${instr}`] || 0);
    if (counts.length === 0) continue;
    const max = Math.max(...counts);
    for (const c of counts) total += max - c;
  }
  return total;
}

// ── DELTA DISTANZA DI FREQUENZA PER BRANO ────────────────────────────────────
// Simula il brano come suonato, ricalcola la distanza, restituisce il delta.
// Delta < 0 = riduce diseguaglianza. Delta > 0 = aumenta diseguaglianza.
export function computeTrackFrequencyDelta(track, playsMap, musiciansMap, currentDistance) {
  const players = track.track_players || [];
  if (!players.length) return 0;

  const simPlays = { ...playsMap };
  for (const player of players) {
    const id    = String(player.musician_id);
    const instr = normalizeInstrumentRole(player.role);
    const key   = `${id}__${instr}`;
    simPlays[key] = (simPlays[key] || 0) + 1;
  }

  return computeCurrentFrequencyDistance(simPlays, musiciansMap) - currentDistance;
}

// ── DRIVER: COVERAGE ─────────────────────────────────────────────────────────
function coverageScore(track) {
  return (track.track_players || []).length;
}

// ── SCORE COMPOSITO ───────────────────────────────────────────────────────────
// Formula: likes + coverage * 2
export function computePriorityScore(track) {
  const likes    = track.likes || 0;
  const coverage = coverageScore(track);
  const score    = likes + coverage * 2;
  return { score, likes, coverage };
}

// ── ORDINAMENTO ───────────────────────────────────────────────────────────────
// Ordina tutti i brani: non suonati per score decrescente, suonati in fondo.
export function sortByPriority(tracks) {
  const played   = tracks.filter(t => t.played);
  const unplayed = tracks.filter(t => !t.played);
  const scored   = unplayed
    .map(t => ({ ...t, _score: computePriorityScore(t) }))
    .sort((a, b) => b._score.score - a._score.score);
  return [...scored, ...played];
}

// Scaletta in ordine di posizione, poi pool per priority score, suonati in fondo.
export function sortByScaletta(tracks) {
  const played   = tracks.filter(t => t.played);
  const unplayed = tracks.filter(t => !t.played);

  const inScaletta = unplayed
    .filter(t => t.in_scaletta)
    .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));

  const inPool = unplayed
    .filter(t => !t.in_scaletta)
    .map(t => ({ ...t, _score: computePriorityScore(t) }))
    .sort((a, b) => b._score.score - a._score.score);

  return [...inScaletta, ...inPool, ...played];
}

// Ordina per delta rotazione crescente (il brano che migliora di piu l'equita va prima),
// a parita usa lo score come spareggio.
export function sortByRotazione(tracks, playsMap, musiciansMap, currentDistance) {
  const played   = tracks.filter(t => t.played);
  const unplayed = tracks.filter(t => !t.played);
  const scored   = unplayed
    .map(t => ({
      ...t,
      _score: computePriorityScore(t),
      _delta: computeTrackFrequencyDelta(t, playsMap, musiciansMap, currentDistance),
    }))
    .sort((a, b) => a._delta - b._delta || b._score.score - a._score.score);
  return [...scored, ...played];
}
